/**
 * A form label with an optional info tip beside it: the how-to-fill text lives
 * in a tooltip instead of under the field. The input lists the hint id in its
 * `aria-describedby`, so assistive tech reads the text with the field.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

const GRACE_MS = 150;
const GAP = 6;
const MARGIN = 8;

/** Hover, focus or a click shows the tip; a second click, Escape, a click outside, a scroll or a resize
 *  hides it. The tip stays while the pointer moves from the trigger to the bubble (WCAG 1.4.13:
 *  dismissible, hoverable, persistent). */
function useInfoTip() {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const visible = hovered || focused || pinned;

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setHovered(false);
    setFocused(false);
    setPinned(false);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Positioned against the viewport like usePopover, so a dialog's scroll box cannot clip it.
  useLayoutEffect(() => {
    if (!visible || !trigger.current) return setPos(null);
    const rect = trigger.current.getBoundingClientRect();
    const width = bubble.current?.offsetWidth ?? 0;
    const height = bubble.current?.offsetHeight ?? 0;
    // Below the trigger, or above it when the viewport has no room underneath.
    const below = rect.bottom + GAP;
    const top = below + height > window.innerHeight - MARGIN && rect.top - GAP - height >= MARGIN ? rect.top - GAP - height : below;
    setPos({ top, left: Math.max(MARGIN, Math.min(rect.left, window.innerWidth - MARGIN - width)) });
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    // Capture phase on window: an open tip takes Escape before the dialog around it sees it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      hide();
    };
    const onMove = (e: Event) => {
      if (!(e.target instanceof Node) || !bubble.current?.contains(e.target)) hide();
    };
    // Safari does not focus a clicked button, so blur alone would leave a click-pinned tip open.
    const onPointer = (e: PointerEvent) => {
      const inside = e.target instanceof Node && (trigger.current?.contains(e.target) || bubble.current?.contains(e.target));
      if (!inside) hide();
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [visible, hide]);

  const enter = () => {
    window.clearTimeout(timer.current);
    setHovered(true);
  };
  const leave = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHovered(false), GRACE_MS);
  };
  // A click pins the tip (it stays after the pointer leaves); a click on a pinned tip hides it, so a
  // touch user can close it with a second tap even though the trigger keeps focus.
  const toggle = () => (pinned ? hide() : setPinned(true));
  const style = visible && pos ? ({ position: 'fixed', top: pos.top, left: pos.left } as const) : undefined;
  return { visible, trigger, bubble, style, enter, leave, setFocused, toggle, unpin: () => setPinned(false) };
}

export function InfoTip({ id, about, children }: { id: string; about: string; children: string }) {
  const t = useInfoTip();
  return (
    <span className="info-tip" onMouseEnter={t.enter} onMouseLeave={t.leave}>
      <button
        ref={t.trigger}
        type="button"
        className="info-tip-trigger"
        aria-label={`About ${about}`}
        aria-describedby={id}
        aria-expanded={t.visible}
        onFocus={() => t.setFocused(true)}
        onBlur={() => {
          t.setFocused(false);
          t.unpin();
        }}
        onClick={t.toggle}
      >
        <Info aria-hidden="true" />
      </button>
      <span ref={t.bubble} id={id} role="tooltip" className={t.visible ? 'info-tip-bubble is-shown' : 'info-tip-bubble'} style={t.style}>
        {children}
      </span>
    </span>
  );
}

export function FieldLabel({ htmlFor, id, label, hint }: { htmlFor: string; id?: string; label: string; hint?: string }) {
  const fallback = useId();
  return (
    <span className="field-label">
      <label htmlFor={htmlFor}>{label}</label>
      {hint && (
        <InfoTip id={id ?? fallback} about={label}>
          {hint}
        </InfoTip>
      )}
    </span>
  );
}
